import locationPinIcon from "assets/images/location-pin-small.svg";
import classNames from "classnames";
import {useEffect, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {appointmentQuestionnaire as strings} from "strings";

import Button from "components/Button";
import ConfirmAppointmentModal from "components/ConfirmAppointmentModal";
import Divider from "components/Divider";
import MobileNavigationHeader from "components/MobileNavigationHeader";
import PageTitle from "components/PageTitle";
import RadioButton from "components/RadioButton";
import TextArea from "components/TextArea";

import * as models from "liberty-core/api/models";

import {useCurrentPharmacy} from "lib/useCurrentPharmacy";

import styles from "./AppointmentQuestionnairePage.module.css";

function QuestionButton({
  sequenceNumber: id,
  type,
  questionText,
  choices: providedAnswers,
  selectedAnswers,
  setUserAnswers,
}: models.AppointmentQuestion & {
  selectedAnswers: {
    [questionSequenceNumber: number]: string;
  };
  setUserAnswers: React.Dispatch<
    React.SetStateAction<{
      [questionSequenceNumber: number]: string;
    }>
  >;
}) {
  return (
    <div>
      <p className={styles.questionLabel}>{questionText}</p>
      {type === "MultipleChoice" || type === "YesOrNo"
        ? (providedAnswers ?? [])
            .sort((a, b) => a.sequenceNumber - b.sequenceNumber)
            .map((answer) => (
              <button
                key={`${answer.sequenceNumber}`}
                className={classNames(styles.row, styles.divButton)}
                onClick={() =>
                  setUserAnswers((prev) => ({
                    ...prev,
                    [id]: answer.choice,
                  }))
                }
              >
                <RadioButton checked={selectedAnswers[id] === answer.choice} />
                <p className={styles.answerLabel}>{answer.choice}</p>
              </button>
            ))
        : null}
      {type === "OpenText" ? (
        <TextArea
          className={classNames(styles.row, styles.openTextInput)}
          placeholder={strings.enterAnswer}
          onChangeText={(text) =>
            setUserAnswers((prev) => ({
              ...prev,
              [id]: text,
            }))
          }
          value={selectedAnswers[id]}
        />
      ) : null}
    </div>
  );
}

type LocationState = {
  guestInfo?: models.AppointmentGuestInfo;
  selectedPatientId: string | null;
  campaignName: string;
  options: models.AppointmentCampaignOption[];
  apptCampaigns: models.AppointmentCampaign[];
  appointmentType: string;
  selectedCampaignId: string;
  selectedCampaignOptionId: string;
  correlationId: string;
  resultQuestions: models.AppointmentQuestion[];
  startDateTime: string;
  insuranceInfo?: {frontImage: File; backImage: File};
  additionalVaccine?: boolean;
};

export default function AppointmentQuestionnairePage() {
  const [pharmacy] = useCurrentPharmacy();
  const location = useLocation();
  const locationState = location.state as LocationState;
  const guestInfo = locationState.guestInfo;
  const selectedPatientId = locationState.selectedPatientId;
  const campaignName = locationState.campaignName;
  const options = locationState.options;
  const appointmentType = locationState.appointmentType;
  const apptCampaigns = locationState.apptCampaigns;
  const selectedCampaignId = locationState.selectedCampaignId;
  const selectedCampaignOptionId = locationState.selectedCampaignOptionId;
  const correlationId = locationState.correlationId;
  const resultQuestions = locationState.resultQuestions;
  const startDateTime = locationState.startDateTime;
  const insuranceInfo = locationState.insuranceInfo;
  const additionalVaccine = locationState.additionalVaccine;

  const [isConfirmationModalVisible, setIsConfirmationModalVisible] =
    useState(false);

  const navigate = useNavigate();

  const sortedAppointmentQuestions = [...resultQuestions].sort(
    (a, b) => a.sequenceNumber - b.sequenceNumber,
  );

  const [userAnswers, setUserAnswers] = useState<{
    [questionSequenceNumber: number]: string;
  }>({});
  useEffect(() => {
    const unsetUserAnswers: {[qsn: number]: string} = {};
    resultQuestions.forEach((q) => (unsetUserAnswers[q.sequenceNumber] = ""));
    setUserAnswers(unsetUserAnswers);
  }, [resultQuestions]);

  useEffect(() => {
    if (!guestInfo && !selectedPatientId) {
      navigate("/appointments", {replace: true});
    }
  }, [navigate, guestInfo, selectedPatientId]);

  return (
    <div className={styles.pageContainer}>
      <MobileNavigationHeader
        onBack={() => {
          if (!additionalVaccine) {
            navigate("/appointments/schedule", {
              state: {
                apptCampaigns: apptCampaigns ?? [],
                guestInfo,
                selectedPatientId,
                appointmentType,
                selectedCampaignId,
                campaignName,
                options,
                selectedCampaignOptionId,
                startDateTime,
                insuranceInfo,
              },
            });
            return;
          }

          const selectedCampaign = selectedCampaignId
            ? apptCampaigns.find((c) => c.id === selectedCampaignId)
            : undefined;
          const hasMoreThanOneOption =
            selectedCampaign?.appointmentOptions?.length &&
            selectedCampaign?.appointmentOptions?.length > 1;

          if (hasMoreThanOneOption) {
            navigate("/appointments/select-option", {
              state: {
                apptCampaigns: apptCampaigns ?? [],
                guestInfo,
                selectedPatientId,
                appointmentType,
                selectedCampaignId,
                campaignName,
                options,
                selectedCampaignOptionId,
                insuranceInfo,
                startDateTime,
              },
            });
          } else {
            navigate("/appointments/select-campaign", {
              state: {
                apptCampaigns: apptCampaigns ?? [],
                guestInfo,
                selectedPatientId,
                appointmentType,
                selectedCampaignId,
                insuranceInfo,
                startDateTime,
              },
            });
          }
        }}
        alwaysShown
      />
      <PageTitle className={styles.header}>{strings.title}</PageTitle>
      <div className={styles.location}>
        <img src={locationPinIcon} alt="" />
        <span className={styles.subtitle}>{pharmacy?.name}</span>
      </div>
      <Divider className={styles.divider} />
      <section className={styles.section}>
        <div className={styles.form}>
          <div>
            {sortedAppointmentQuestions.map((question, questionIndex) => {
              return (
                <div key={question.sequenceNumber}>
                  {questionIndex === 0 ? null : <Divider />}
                  <QuestionButton
                    {...question}
                    selectedAnswers={userAnswers}
                    setUserAnswers={setUserAnswers}
                  />
                </div>
              );
            })}
          </div>
          <Button
            className={styles.button}
            disabled={Object.keys(userAnswers).some(
              (questionSequenceNumber) =>
                !userAnswers[parseInt(questionSequenceNumber, 10)],
            )}
            onClick={() => {
              setIsConfirmationModalVisible(true);
            }}
          >
            {strings.review}
          </Button>
          <ConfirmAppointmentModal
            visible={isConfirmationModalVisible}
            onClose={() => setIsConfirmationModalVisible(false)}
            guestInfo={guestInfo}
            selectedPatientId={selectedPatientId}
            campaignName={campaignName}
            selectedCampaignOptionId={selectedCampaignOptionId}
            correlationId={correlationId}
            startDateTime={startDateTime}
            responses={Object.keys(userAnswers).map((k) => {
              const questionSequenceNumber = parseInt(k, 10);
              return {
                questionNumber: questionSequenceNumber,
                value: userAnswers[questionSequenceNumber],
              };
            })}
            insuranceInfo={insuranceInfo}
            appointmentType={appointmentType}
          />
        </div>
      </section>
    </div>
  );
}
